import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useMemo, useRef, useContext } from 'react';
import { Modal, Fade } from '@material-ui/core';
import cn from 'classnames';
import { overlayZIndex } from '../../../foundations/zIndex.constants';
import { DeviceProvider } from '../../../providers/Device';
import { getTestIds } from '../../../utils/TestIds';
import { BiModalContainer } from '../../BiTracking';
import { useBottomSheetContainer } from '../../BottomSheet/BottomSheetContainerContext';
import styles from '../OverlayContext.module.scss';
import { OverlayPopupZIndex } from '../OverlayPopupZIndex/OverlayPopupZIndex';
export const GetModalContainerTID = (parentTestId)=>getTestIds(parentTestId);
const IsInModalContext = /*#__PURE__*/ React.createContext(false);
export const useIsInModal = ()=>useContext(IsInModalContext);
/** Full-page surfaces wrap their children with value=false — full-page content has page semantics even when hosted by showModal. */ export const IsInModalProvider = IsInModalContext.Provider;
const ModalHostContext = /*#__PURE__*/ React.createContext({
    setDetached: ()=>{},
    setOwnsFullScreen: ()=>{}
});
/** Lets a surface that portals its presentation elsewhere (e.g. into the managed sheet stack) hide its hosting ModalContainer chrome. */ export const useModalHost = ()=>useContext(ModalHostContext);
/** Test seam: lets a driver observe what a surface reports to the modal hosting it. */ export const ModalHostProvider = ModalHostContext.Provider;
const getTransitionDuration = (isTestMode)=>isTestMode ? 0 : 225;
const DefaultModalWrapper = (param)=>{
    let { children } = param;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
};
export function ModalEntryContent(param) {
    let { entry, modalProps, ModalWrapper = DefaultModalWrapper, modalHost, isInModal = true } = param;
    const body = /*#__PURE__*/ _jsx(BiModalContainer, _object_spread_props(_object_spread({}, entry.noBi ? {
        noBi: entry.noBi,
        biCallerContext: entry.biCallerContext
    } : {
        biName: entry.biName,
        biData: entry.biData,
        biCallerContext: entry.biCallerContext
    }), {
        children: /*#__PURE__*/ _jsx(ModalWrapper, _object_spread_props(_object_spread({}, modalProps), {
            children: /*#__PURE__*/ _jsx(IsInModalContext.Provider, {
                value: isInModal,
                children: /*#__PURE__*/ _jsx(ModalHostContext.Provider, {
                    value: modalHost,
                    children: entry.content && (typeof entry.content === 'function' ? entry.content(modalProps) : /*#__PURE__*/ React.cloneElement(entry.content, modalProps))
                })
            })
        }))
    }));
    return entry.device ? /*#__PURE__*/ _jsx(DeviceProvider, {
        value: entry.device,
        children: body
    }) : body;
}
const makeModalCustomBackdrop = (isTestMode)=>{
    return (param)=>{
        let { open, onClick, modal } = param;
        const FadeComp = Fade;
        return /*#__PURE__*/ _jsx(FadeComp, {
            timeout: getTransitionDuration(isTestMode),
            in: open,
            onClick: onClick,
            children: /*#__PURE__*/ _jsx("div", {
                className: cn(styles.modalBackdrop, {
                    [styles.modalBg]: !modal.transparentBackdrop
                })
            })
        });
    };
};
export function ModalContainer(param) {
    let { modal, zIndexOrder = 0, ModalWrapper = DefaultModalWrapper, isTestMode, disableEnforceFocus, triggerClose, onModalExited } = param;
    const { ModalContainerTID } = useMemo(()=>GetModalContainerTID(), []);
    const [returnValue, setReturnValue] = useState(undefined);
    const [detached, setDetached] = useState(false);
    const [ownsFullScreen, setOwnsFullScreen] = useState(false);
    const modalHostValue = useMemo(()=>({
            setDetached,
            setOwnsFullScreen
        }), []);
    const canDismissModal = useRef();
    const registerCanDismiss = (cb)=>canDismissModal.current = cb;
    const BackdropComponent = useMemo(()=>makeModalCustomBackdrop(isTestMode), [
        isTestMode
    ]);
    const { container } = useBottomSheetContainer();
    const hostZIndex = overlayZIndex(zIndexOrder);
    // WHY: MUI's Modal root is position:fixed, which escapes a scoping container — pin it inside.
    const modalRootStyle = useMemo(()=>_object_spread({
            zIndex: hostZIndex
        }, detached && {
            visibility: 'hidden'
        }, container != null && {
            position: 'absolute'
        }), [
        detached,
        container,
        hostZIndex
    ]);
    const dismissModal = async function dismissModal(value) {
        let { force } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        if (canDismissModal.current && !force) {
            const confirmed = await canDismissModal.current();
            if (!confirmed) return;
        }
        setReturnValue(value);
        triggerClose(modal.id);
    };
    modal.dismissModal = dismissModal;
    const modalProps = {
        dismissModal,
        registerCanDismiss
    };
    return /*#__PURE__*/ _jsx(Modal, {
        open: modal.isOpen,
        container: container !== null && container !== void 0 ? container : undefined,
        disableScrollLock: container != null,
        style: modalRootStyle,
        disableEnforceFocus: disableEnforceFocus || modal.disableEnforceFocus || detached,
        closeAfterTransition: true,
        BackdropComponent: BackdropComponent,
        BackdropProps: {
            modal
        },
        onClose: (e, reason)=>{
            if (reason === 'escapeKeyDown' && modal.closeOnEscapeKey) {
                dismissModal();
            }
        },
        children: /*#__PURE__*/ _jsx(Fade, {
            timeout: getTransitionDuration(isTestMode),
            in: modal.isOpen,
            onExited: ()=>{
                modal.resolve(returnValue);
                onModalExited(modal.id);
            },
            children: /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.modalContainer, {
                    [styles.nativeSafeAreaInset]: !ownsFullScreen
                }),
                style: _object_spread_props(_object_spread({}, modal.theme), {
                    pointerEvents: modal.isOpen ? 'auto' : 'none'
                }),
                "data-testid": ModalContainerTID.Root(),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.modalBackdrop,
                        "data-testid": ModalContainerTID.Backdrop(),
                        onClick: ()=>{
                            if (modal.closeOnClickOutside) {
                                dismissModal();
                            }
                        }
                    }),
                    /*#__PURE__*/ _jsx(OverlayPopupZIndex, {
                        hostZIndex: hostZIndex,
                        children: /*#__PURE__*/ _jsx(ModalEntryContent, {
                            entry: modal,
                            modalProps: modalProps,
                            ModalWrapper: ModalWrapper,
                            modalHost: modalHostValue
                        })
                    })
                ]
            })
        })
    });
}
